import { formatDate, isPlatformBrowser } from "@angular/common";
import {
	type AfterViewInit,
	DestroyRef,
	Directive,
	ElementRef,
	Inject,
	Input,
	LOCALE_ID,
	NgZone,
	PLATFORM_ID,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { auditTime } from "rxjs/operators";

@Directive({ selector: "[obgOmsLocalizeTime]" })
export class OmsLocalizeTimeDirective implements AfterViewInit {
	@Input({ alias: "obgOmsLocalizeTime" }) config: { format?: string; timezone?: string } = {};

	private readonly enhance$ = new Subject<void>();

	private mutationObserver: MutationObserver | null = null;

	constructor(
		/* eslint-disable-next-line @typescript-eslint/ban-types */
		@Inject(PLATFORM_ID) private readonly platformId: Object,
		@Inject(LOCALE_ID) private readonly locale: string,
		private readonly destroyRef: DestroyRef,
		private readonly ngZone: NgZone,
		readonly elementRef: ElementRef
	) {}

	ngAfterViewInit(): void {
		this.enhance();

		/* eslint-disable rxjs/no-ignored-subscription */
		this.enhance$
			.asObservable()
			.pipe(auditTime(250), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.enhance());
		/* eslint-enable rxjs/no-ignored-subscription */

		if (isPlatformBrowser(this.platformId)) {
			this.ngZone.runOutsideAngular(() => {
				const mutationObserver = new MutationObserver(() => this.enhance$.next());

				mutationObserver.observe(this.elementRef.nativeElement, {
					subtree: true,
					childList: true,
					attributes: true,
					attributeFilter: ["datetime"],
				});

				this.mutationObserver = mutationObserver;
			});
		}

		this.destroyRef.onDestroy(() => this.mutationObserver != null && this.mutationObserver.disconnect());
	}

	private enhance(): void {
		const { format, timezone } = this.config || {};

		Array.from(this.elementRef.nativeElement.querySelectorAll("time[datetime]")).forEach(
			(element: HTMLTimeElement) => {
				const utcValue: string = element?.dateTime?.trim() ?? "";

				if (!utcValue) {
					return;
				}

				const currentValue: string = element?.textContent?.trim() ?? "";
				const nextValue: string = formatDate(utcValue, format || "short", this.locale, timezone);

				if (!nextValue || Object.is(currentValue, nextValue)) {
					return;
				}

				element.innerText = nextValue;
			}
		);
	}
}
